import React, { useState, useEffect } from "react";
import {
  Menu,
  MenuItem,
  Select,
  Box,
  useTheme,
} from "@mui/material";
import { SetURLSearchParams } from "react-router-dom";
import { GetFilterByIdDto } from "@bandido/shared/dtos";
import { useTranslation } from "react-i18next";
import { arrayToPipeDelimited } from "../../helper";
import { useSessionFilters } from "@frontend/features/cases/hooks/useSessionFilters";

interface Props {
  privateFilters: GetFilterByIdDto[];
  searchParams?: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export const MyFiltersMenu = ({ privateFilters, searchParams, setSearchParams }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { updateFilters } = useSessionFilters(searchParams ?? new URLSearchParams(), setSearchParams);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilter, setSelectedFilter] = useState<GetFilterByIdDto>();

  useEffect(() => {
    const params = searchParams ?? new URLSearchParams();
    const hasAnyFilters = Array.from(params.entries()).some(([key, value]) => 
      !['sort', 'order', 'page', 'search'].includes(key) && value && value.trim() !== ''
    );
    
    if (!hasAnyFilters && selectedFilter) {
      setSelectedFilter(undefined);
    }
  }, [searchParams, selectedFilter]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (filter: GetFilterByIdDto) => {
    setSelectedFilter(filter);
    
    const objCleanParams = {
      search: '',
      status: '',
      teamIds: '',
      workflowIds: '',
      stepIds: '',
      clientIds: '',
      agentIds: '',
      order: '',
    };

    const urlParams = arrayToPipeDelimited(filter.params);

    updateFilters({
      ...objCleanParams,
      ...urlParams
    });

    handleClose();
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Select
        value={selectedFilter}
        displayEmpty
        onClick={handleOpen}
        size="small"
        variant="outlined"
        fullWidth
        readOnly
        sx={{
          color: theme.palette.disabled,
          ">.MuiSelect-outlined": {
            opacity: 0.5
          },
        }}
      >
        <MenuItem
          value={undefined}
          disabled={true}
        >
          {selectedFilter ? selectedFilter.name : t('cases.list.filters.myFilters')}
        </MenuItem>
      </Select>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {privateFilters.map((filter) => (
          <MenuItem key={filter.id} onClick={() => handleSelect(filter)}>
            {filter.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
